import React, { useState, useMemo, useEffect } from "react";
import _ from "lodash";
import {
  AlertComponent,
  BreadcrumbComponent,
  ConfirmationPopup,
  PaginationComponent,
  SearchInputComponent,
  SelectInputComponent,
} from "../../../common/components";
import { routes } from "../../../constants/routes";
import { useSetLoading } from "../../../common/hooks/appearance";
import createleadtext from "../../../assets/icons/creteleadtext.svg";
import createleadvoice from "../../../assets/icons/creteleadvoice.svg";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import "./notes-list.scss";
import editNoteIcon from "../../../assets/icons/editNoteIcon.svg";
import deleteNoteIcon from "../../../assets/icons/deleteNoteIcon.svg";
import shareNoteIcon from "../../../assets/icons/shareNoteIcon.svg";
import VoiceNoteDialog from "./voice-note-dialog";
import {
  archiveNote,
  getNotes,
  noteCreateOrUpdate,
  shareNote,
} from "../../../api/notes";
import { convertUTCToLocalRelative } from "../../../utils/utils";
import { useLogin } from "../../../common/hooks/useUser";
import { getUserDetails } from "../../../api/lead";
import { getTenantsList } from "../../../api/tenets";
import EmailSetUpBanner from "../../../common/components/email-setup-required-banner/email-setup-banner";
import { ErrorMessage } from "formik";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";

const NotesList = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [notesData, setNotesData] = React.useState<Array<any>>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(5);
  const [searchNote, setSearchNote] = useState<string>("");
  const isSearchTriggered = React.useRef<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchor);
  const [selectedNoteType, setSelectedNoteType] = useState<string>("All Notes");
  const [notesFilter, setNotesFilter] = useState<any>();
  const [showAddTextNoteModal, setShowAddTextNoteModal] =
    useState<boolean>(false);
  const [showVoiceNoteDialog, setShowVoiceNoteDialog] = useState(false);
  const [mode, setMode] = useState<"Add" | "Edit" | null>(null);
  const [editNoteId, setEditNoteId] = useState<number | null>(null);
  const [notesContent, setNotesContent] = useState<string>("");
  const [notesType, setNotesType] = useState<string>("");
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiveNoteId, setArchiveNoteId] = useState<number | null>(null);
  const [shareNoteId, setShareNoteId] = useState<number | null>(null);
  const [shareNoteDialogOpen, setShareNoteDialogOpen] = useState(false);
  const [usersData, setUsersData] = useState<Array<any>>([]);
  const [selectedUsers, setSelectedUsers] = useState<Array<any>>([]);
  const [searchUserText, setSearchUserText] = useState<string>("");
  const getUserKey = (user: any) => `${user.companyName}_${user.email}`;

  const loginDetails = useLogin();
  const tenantDetails = loginDetails?.tenantDetails;
  const isEmailConfigured = loginDetails?.isEmailConfigured;
  const isAFPSuperTenant = tenantDetails?.tenantType === "SuperTenant";
  const isSuperAdmin = loginDetails?.userDetails?.roles?.role === "Super Admin";
  const { canWrite, canDelete } = useResourcePermissions(MODULES.GENERAL_NOTES);

  const notesFilterOptions = [
    { id: "all", value: "All Notes" },
    { id: "MANUAL", value: "Manual Note" },
    { id: "TRANSCRIBED", value: "Transcribed Note" },
  ];

  const breadcrumbLink = [
    {
      label: "Templates & QRs",
      link: "",
    },
    {
      label: "General Notes",
      link: routes.GENERAL_NOTES,
      handleClick: () => {},
    },
  ];

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleSearch = _.debounce((text: string) => {
    isSearchTriggered.current = true;
    setCurrentPage(1);
    setSearchNote(text);
  }, 500);

  const handleUserSearch = useMemo(
    () =>
      _.debounce((text: string) => {
        setSearchUserText(text);
      }, 500),
    []
  );

  useEffect(() => {
    if (shareNoteDialogOpen) {
      fetchUsers();
    }
  }, [searchUserText, shareNoteDialogOpen]);

  const addNotesButtonOptions = () => {
    return [
      {
        value: "Text",
        id: 1,
        icon: createleadtext,
      },
      {
        value: "Voice",
        id: 2,
        icon: createleadvoice,
      },
    ];
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const handleAddNotes = (selectedOption: { value: string; id: number }) => {
    if (selectedOption.value === "Text") {
      setShowAddTextNoteModal(true);
      setNotesType("MANUAL");
      setMode("Add");
    }
    if (selectedOption.value === "Voice") {
      setShowVoiceNoteDialog(true);
      setNotesType("TRANSCRIBED");
    }

    handleClose();
  };

  const addOrUpdateNoteHandler = async (text?: string) => {
    const content = (text ?? notesContent).trim();
    if (!content) {
      setAlertMessage("Text should not be empty.");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        note: content,
        noteType: notesType,
      };
      await noteCreateOrUpdate(payload, editNoteId ?? undefined);
      fetchNotesList();
      closeAddEditDialog();
      setAlertMessage(
        `Note ${mode === "Add" ? "added" : "updated"} successfully`
      );
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to add note."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const archiveNoteHandler = async () => {
    setIsLoading(true);
    try {
      if (!archiveNoteId) return;
      await archiveNote(archiveNoteId);
      fetchNotesList();
      setArchiveConfirmOpen(false);
      setAlertMessage("Note archived successfully");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to archive note."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      setUsersData([]);
      const params: any = {
        status: "ACTIVE",
        ignorePagination: true,
      };
      if (isAFPSuperTenant) {
        params.includeAllCompanyUsers = true;
      }
      if (searchUserText && searchUserText.trim().length > 0) {
        params.searchText = searchUserText;
      }
      const response = await getUserDetails(params);
      setUsersData(response.data.data.data.data);
    } catch (error) {
      console.log("Error fetching users: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const shareNoteHandler = async () => {
    setIsLoading(true);
    try {
      if (!shareNoteId) return;
      const payload = {
        noteId: shareNoteId,
        usersData: selectedUsers.map((u) => ({
          email: u.email,
          companyName: u.companyName,
          first_name: u.first_name,
          last_name: u.last_name,
          role: u.role.role,
          id: u.id,
          tenantId: u.tenantId,
        })),
      };
      await shareNote(shareNoteId, payload);
      setAlertMessage("Note shared successfully");
      setAlertType("success");
      setAlertOpen(true);
      setShareNoteDialogOpen(false);
      setSelectedUsers([]);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to share note."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeShareNoteDialog = () => {
    setShareNoteDialogOpen(false);
    setSelectedUsers([]);
    setSearchUserText("");
  };

  const closeArchiveConfirm = () => {
    setArchiveConfirmOpen(false);
    setArchiveNoteId(null);
  };

  const closeAddEditDialog = () => {
    setShowAddTextNoteModal(false);
    setShowVoiceNoteDialog(false);
    setMode(null);
    setNotesContent("");
    setEditNoteId(null);
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const fetchNotesList = async (
    page = 1,
    searchNote: string = "",
    notesFilter?: any
  ) => {
    setIsLoading(true);
    const offset = (page - 1) * pageSize;
    const params: any = {
      limit: pageSize,
      offset: offset,
    };
    if (searchNote && searchNote.trim().length > 0) {
      params.searchNote = searchNote;
    }
    if (notesFilter) {
      params.noteType = notesFilter;
    }
    try {
      const response = await getNotes(params);
      setNotesData(response.data.data.data);
      setTotalCount(response.data.data.totalRecords || 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to fetch notes."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotesList(currentPage, searchNote, notesFilter);
  }, [currentPage, searchNote, selectedNoteType, notesFilter]);

  return (
    <div className="notes-list-container">
      <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
      <div className="notes-list-header">
        <div className="notes-list-search">
          <SearchInputComponent placeholder="Search" onSearch={handleSearch} />
        </div>
        <div className="notes-list-header-right-section">
          <div className="notes-list-type-filter">
            <SelectInputComponent
              value={selectedNoteType || "All Notes"}
              options={notesFilterOptions}
              onChange={(e: any) => {}}
              handleOption={(e: any) => {
                const noteType =
                  e?.value === "Manual Note"
                    ? "MANUAL"
                    : e?.value === "All Notes"
                      ? undefined
                      : "TRANSCRIBED";
                setSelectedNoteType(e?.value);
                setNotesFilter(noteType);
              }}
            />
          </div>
          <div
            className={`notes-list-add-note-button ${!canWrite ? "disabled" : ""}`}
          >
            <Button
              variant="contained"
              onClick={handleClick}
              className={`custom-button ${!canWrite ? "disabled" : ""}`}
              disabled={!canWrite}
            >
              Add Notes
            </Button>
            <Menu anchorEl={menuAnchor} open={open} onClose={handleClose}>
              {addNotesButtonOptions().map((option) => (
                <MenuItem
                  key={option.id}
                  onClick={() => handleAddNotes(option)}
                >
                  <img src={option?.icon} alt={option?.icon} />
                  {option.value}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>
      </div>
      {!isEmailConfigured && isSuperAdmin && (
        <div className="email-setup-banner-wrapper">
          <EmailSetUpBanner />
        </div>
      )}
      <div className="notes-list">
        {notesData && notesData.length > 0 ? (
          notesData.map((note: any) => {
            const createdByFirstName = note.createdBy?.first_name;
            const createdByLastName = note.createdBy?.last_name;
            const createdByName =
              createdByFirstName && createdByLastName
                ? `${createdByFirstName} ${createdByLastName}`
                : createdByFirstName
                  ? createdByFirstName
                  : createdByLastName
                    ? createdByLastName
                    : "-";
            return (
              <div key={note.id} className="note-item">
                <div className="note-content">{note?.notes}</div>
                <div className="note-meta">
                  <div className="notes-left-meta">
                    <span>{convertUTCToLocalRelative(note.updated_at)}</span>
                    <span>Created by: {createdByName}</span>
                    <div
                      className="note-type"
                      style={{
                        backgroundColor:
                          note.note_type === "TRANSCRIBED"
                            ? "#dbeafe"
                            : "#F3E8FF",
                        borderColor:
                          note.note_type === "TRANSCRIBED"
                            ? "#bfcfff"
                            : "#E9D4FF",
                      }}
                    >
                      <p
                        style={{
                          color:
                            note.note_type === "TRANSCRIBED"
                              ? "#1447E6"
                              : "#8200DB",
                        }}
                      >
                        {note?.note_type === "TRANSCRIBED"
                          ? "Transcribed Note"
                          : "Manual Note"}
                      </p>
                    </div>
                  </div>
                  <div className="notes-right-meta">
                    <div
                      className={`icon-wrapper ${!canWrite ? "disabled" : ""}`}
                    >
                      <img
                        src={editNoteIcon}
                        alt="Edit Note"
                        className={`edit-note-icon ${!canWrite ? "disabled" : ""}`}
                        onClick={() => {
                          if (!canWrite) return;
                          setMode("Edit");
                          setEditNoteId(note.id);
                          setNotesContent(note.notes);
                          setShowAddTextNoteModal(true);
                          setNotesType(note.note_type);
                        }}
                      />
                    </div>
                    <div
                      className={`icon-wrapper ${!canDelete ? "disabled" : ""}`}
                    >
                      <img
                        src={deleteNoteIcon}
                        alt="Delete Note"
                        className={`delete-note-icon ${!canDelete ? "disabled" : ""}`}
                        onClick={() => {
                          if (!canDelete) return;
                          setArchiveNoteId(note.id);
                          setArchiveConfirmOpen(true);
                        }}
                      />
                    </div>
                    <div
                      className={`icon-wrapper`}
                      onClick={() => {
                        setShareNoteDialogOpen(true);
                        setShareNoteId(note.id);
                      }}
                    >
                      <img
                        src={shareNoteIcon}
                        alt="Share Note"
                        className={`share-note-icon`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-notes-message">No notes found.</div>
        )}
      </div>

      {totalCount > 0 && (
        <div>
          <PaginationComponent
            currentPage={currentPage}
            totalRecords={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </div>
      )}
      <Dialog
        open={showAddTextNoteModal}
        maxWidth="sm"
        fullWidth
        className="notes-dialog"
      >
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {mode} Note
          <IconButton
            aria-label="close"
            onClick={closeAddEditDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ marginTop: 2 }}>
          <div>Note Text</div>
          <TextField
            fullWidth
            multiline
            rows={4}
            className="notes-text-field"
            placeholder="Enter note text..."
            value={notesContent}
            onChange={(e) => setNotesContent(e.target.value)}
          />
          {mode === "Edit" && !notesContent.trim() && (
            <div className="error-message">Text should not be empty.</div>
          )}
        </DialogContent>
        <DialogActions className="notes-dialog-actions-buttons">
          <Button onClick={closeAddEditDialog} className="cancel-button">
            Cancel
          </Button>
          <Button
            className="submit-button"
            disabled={!notesContent.trim()}
            onClick={() => addOrUpdateNoteHandler()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <VoiceNoteDialog
        open={showVoiceNoteDialog}
        onClose={closeAddEditDialog}
        onSave={(text: string) => {
          setShowVoiceNoteDialog(false);
          addOrUpdateNoteHandler(text);
        }}
      />
      <ConfirmationPopup
        open={archiveConfirmOpen}
        onCancel={closeArchiveConfirm}
        onOkay={archiveNoteHandler}
        title="Archive Note"
        note={"Are you sure you want to archive this note?"}
        buttonText="Archive"
      />
      <Dialog
        open={shareNoteDialogOpen}
        maxWidth="sm"
        fullWidth
        className="notes-dialog"
      >
        <DialogTitle className="share-note-title">
          Share Note
          <div className="share-note-subtitle">
            Select team members to share this note with
          </div>
          <IconButton
            aria-label="close"
            onClick={closeShareNoteDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="users-search">
            <SearchInputComponent
              placeholder="Search users to share note"
              onSearch={handleUserSearch}
            />
          </div>
          {selectedUsers.length > 0 && (
            <div className="selected-users-count">
              {selectedUsers.length} user
              {selectedUsers.length > 1 ? "s" : ""} selected
            </div>
          )}
          <div className="users-list">
            {usersData?.length > 0 ? (
              usersData.map((user) => {
                const initials = `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`;
                const isSelected = selectedUsers.some(
                  (u) => getUserKey(u) === getUserKey(user)
                );
                return (
                  <div
                    key={user.id}
                    className={`user-item ${isSelected ? "selected" : ""}`}
                  >
                    <div className="user-info">
                      <div
                        className={`user-avatar ${isSelected ? "selected" : ""}`}
                      >
                        {initials.toUpperCase()}
                      </div>
                      <div className="user-details">
                        <div className="user-name">
                          {user.first_name} {user.last_name}
                          {user.companyName && (
                            <span className="company-name">
                              {user.companyName}
                            </span>
                          )}
                        </div>
                        <div className="user-email">{user.email}</div>
                        <div className="user-role">{user.role.role}</div>
                      </div>
                    </div>
                    <div className="user-action">
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers((prev) => [...prev, user]);
                          } else {
                            setSelectedUsers((prev) =>
                              prev.filter(
                                (u) => getUserKey(u) !== getUserKey(user)
                              )
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-users-message">No users found.</div>
            )}
          </div>
        </DialogContent>
        <DialogActions className="notes-dialog-actions-buttons">
          <div className="select-users-text">
            {selectedUsers.length > 0
              ? `sharing with ${selectedUsers.length} user${selectedUsers.length > 1 ? "s" : ""}`
              : "Select users to share this note"}
          </div>
          <Button onClick={closeShareNoteDialog} className="cancel-button">
            Cancel
          </Button>
          <Button
            className="submit-button"
            onClick={shareNoteHandler}
            disabled={selectedUsers.length === 0}
          >
            Share Note
          </Button>
        </DialogActions>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default NotesList;
